import React, { useState } from 'react';
import { Button } from './ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter 
} from './ui/dialog';
import { MapPin, Navigation, CheckCircle, XCircle, Loader2, AlertTriangle, Settings } from 'lucide-react';

const GPSPermissionModal = ({ open, onClose, onSuccess, onError }) => {
  const [step, setStep] = useState('intro'); // intro, requesting, success, error, denied
  const [errorMessage, setErrorMessage] = useState('');
  const [position, setPosition] = useState(null);

  const requestGPSPermission = async () => {
    setStep('requesting');

    // Vérifier si la géolocalisation est supportée
    if (!navigator.geolocation) {
      setStep('error');
      setErrorMessage('Votre navigateur ne supporte pas la géolocalisation.');
      return;
    }

    try {
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          (error) => {
            switch(error.code) {
              case error.PERMISSION_DENIED:
                reject({ type: 'PERMISSION_DENIED' });
                break;
              case error.POSITION_UNAVAILABLE:
                reject({ type: 'POSITION_UNAVAILABLE' });
                break;
              case error.TIMEOUT:
                reject({ type: 'TIMEOUT' });
                break;
              default:
                reject({ type: 'UNKNOWN' });
            }
          },
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
        );
      });

      setPosition({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy
      });
      setStep('success');
      
      // Attendre un peu pour montrer le succès puis continuer
      setTimeout(() => {
        onSuccess(pos.coords.latitude, pos.coords.longitude);
      }, 1500);

    } catch (error) {
      if (error.type === 'PERMISSION_DENIED') {
        setStep('denied');
      } else if (error.type === 'POSITION_UNAVAILABLE') {
        setStep('error');
        setErrorMessage('Le GPS est désactivé sur votre téléphone. Activez-le dans vos paramètres rapides.');
      } else if (error.type === 'TIMEOUT') {
        setStep('error');
        setErrorMessage('Le GPS met du temps à répondre. Sortez à l\'extérieur si vous êtes en sous-sol.');
      } else {
        setStep('error');
        setErrorMessage('Impossible de récupérer votre position.');
      }
    }
  };

  const handleClose = () => {
    setStep('intro');
    setErrorMessage('');
    setPosition(null);
    onClose();
  };

  const handleRetry = () => {
    setStep('intro');
    setErrorMessage('');
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        {/* Étape 1: Introduction */}
        {step === 'intro' && (
          <>
            <DialogHeader>
              <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <MapPin className="h-8 w-8 text-blue-600" />
              </div>
              <DialogTitle className="text-center text-xl">
                Autoriser la localisation
              </DialogTitle>
              <DialogDescription className="text-center mt-2">
                Pour valider votre arrivée sur le logement, l'application a besoin d'accéder à votre position GPS.
              </DialogDescription>
            </DialogHeader>
            
            <div className="bg-blue-50 rounded-lg p-4 my-4">
              <p className="text-sm text-blue-800 font-medium mb-2">Pourquoi c'est nécessaire ?</p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Vérifier que vous êtes bien sur place</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Protéger contre les pointages frauduleux</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Garantir une facturation correcte</span>
                </li>
              </ul>
            </div>

            <DialogFooter className="flex-col gap-2 sm:flex-col">
              <Button 
                onClick={requestGPSPermission}
                className="w-full h-12 bg-[#1F3A8A] hover:bg-[#162d6d]"
              >
                <Navigation className="h-5 w-5 mr-2" />
                Autoriser ma localisation
              </Button>
              <Button 
                variant="outline" 
                onClick={handleClose}
                className="w-full"
              >
                Annuler
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Étape 2: En cours de demande */}
        {step === 'requesting' && (
          <>
            <DialogHeader>
              <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 animate-pulse">
                <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
              </div>
              <DialogTitle className="text-center text-xl">
                Récupération de votre position...
              </DialogTitle>
              <DialogDescription className="text-center mt-2">
                Veuillez patienter quelques secondes.
                <br />
                <strong className="text-blue-600">Si une popup apparaît, cliquez sur "Autoriser"</strong>
              </DialogDescription>
            </DialogHeader>
            
            <div className="bg-amber-50 rounded-lg p-4 my-4">
              <p className="text-sm text-amber-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                <span>Une fenêtre peut apparaître pour demander l'autorisation</span>
              </p>
            </div>
          </>
        )}

        {/* Étape 3: Succès */}
        {step === 'success' && (
          <>
            <DialogHeader>
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <DialogTitle className="text-center text-xl text-green-700">
                Position récupérée !
              </DialogTitle>
              <DialogDescription className="text-center mt-2">
                Votre localisation a été vérifiée avec succès.
              </DialogDescription>
            </DialogHeader>
            
            {position && (
              <div className="bg-green-50 rounded-lg p-4 my-4 text-center">
                <p className="text-sm text-green-700">
                  Précision : ±{Math.round(position.accuracy)}m
                </p>
              </div>
            )}
          </>
        )}

        {/* Étape 4: Permission refusée */}
        {step === 'denied' && (
          <>
            <DialogHeader>
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <DialogTitle className="text-center text-xl text-red-700">
                Accès refusé
              </DialogTitle>
              <DialogDescription className="text-center mt-2">
                Vous devez autoriser l'accès à votre position pour continuer.
              </DialogDescription>
            </DialogHeader>
            
            <div className="bg-amber-50 rounded-lg p-4 my-4">
              <p className="text-sm text-amber-900 font-semibold mb-2 flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Comment autoriser ?
              </p>
              <ol className="text-sm text-amber-800 space-y-2 list-decimal ml-5">
                <li>Ouvrez les <strong>Paramètres</strong> de votre téléphone</li>
                <li>Allez dans <strong>Applications</strong> → <strong>PlayMCB</strong></li>
                <li>Cliquez sur <strong>Autorisations</strong></li>
                <li>Activez <strong>Localisation</strong> → <strong>Autoriser</strong></li>
                <li>Revenez dans l'application et réessayez</li>
              </ol>
            </div>

            <DialogFooter className="flex-col gap-2 sm:flex-col">
              <Button 
                onClick={handleRetry}
                className="w-full h-12 bg-[#1F3A8A] hover:bg-[#162d6d]"
              >
                Réessayer
              </Button>
              <Button 
                variant="outline" 
                onClick={handleClose}
                className="w-full"
              >
                Fermer
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Étape 5: Erreur */}
        {step === 'error' && (
          <>
            <DialogHeader>
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
              <DialogTitle className="text-center text-xl text-red-700">
                Problème GPS
              </DialogTitle>
              <DialogDescription className="text-center mt-2">
                {errorMessage}
              </DialogDescription>
            </DialogHeader>
            
            <div className="bg-blue-50 rounded-lg p-4 my-4">
              <p className="text-sm text-blue-800 font-medium mb-2">Conseils :</p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Activez le GPS dans vos paramètres rapides (glissez vers le bas)</li>
                <li>• Sortez à l'extérieur si vous êtes en sous-sol</li>
                <li>• Attendez quelques secondes que le GPS se synchronise</li>
              </ul>
            </div>

            <DialogFooter className="flex-col gap-2 sm:flex-col">
              <Button 
                onClick={handleRetry}
                className="w-full h-12 bg-[#1F3A8A] hover:bg-[#162d6d]"
              >
                Réessayer
              </Button>
              <Button 
                variant="outline" 
                onClick={handleClose}
                className="w-full"
              >
                Fermer
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GPSPermissionModal;
